import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/hooks/use-toast'
import { Loader2 } from 'lucide-react'
import type { User } from './UserRoleTable'

interface AssignSpecialistDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  user: User
  onSuccess: () => void
}

interface Specialist {
  id: string
  name: string
  email: string
}

export function AssignSpecialistDialog({
  open,
  onOpenChange,
  user,
  onSuccess,
}: AssignSpecialistDialogProps) {
  const { toast } = useToast()
  const [specialists, setSpecialists] = useState<Specialist[]>([])
  const [selectedSpecialistId, setSelectedSpecialistId] = useState<string>('')
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(false)
  const [fetchingSpecialists, setFetchingSpecialists] = useState(true)

  // Fetch all specialists
  useEffect(() => {
    const fetchSpecialists = async () => {
      try {
        const { data, error } = await supabase
          .from('specialists')
          .select('id, name, email')
          .order('name')

        if (error) throw error
        setSpecialists(data || [])
      } catch (error) {
        console.error('Error fetching specialists:', error)
        toast({
          variant: 'destructive',
          title: 'Error',
          description: 'Failed to load specialists',
        })
      } finally {
        setFetchingSpecialists(false)
      }
    }

    if (open) {
      fetchSpecialists()
      // Pre-select current specialist if assigned
      if (user.specialist_id) {
        setSelectedSpecialistId(user.specialist_id)
      }
    }
  }, [open, user.specialist_id, toast])

  const handleAssign = async () => {
    if (!selectedSpecialistId) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Please select a specialist',
      })
      return
    }

    if (!user.caregiver_id) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Caregiver ID not found',
      })
      return
    }

    setLoading(true)

    try {
      // Check if there's an existing assignment
      const { data: existingAssignment } = await supabase
        .from('specialist_caregiver')
        .select('id, specialist_id')
        .eq('caregiver_id', user.caregiver_id)
        .eq('archived', false)
        .single()

      if (existingAssignment) {
        // Transfer to new specialist
        const { error: transferError } = await supabase.rpc(
          'transfer_caregiver_to_specialist' as any,
          {
            p_caregiver_id: user.caregiver_id,
            p_old_specialist_id: existingAssignment.specialist_id,
            p_new_specialist_id: selectedSpecialistId,
            p_reason: reason || 'Reassigned from admin panel',
          }
        )

        if (transferError) throw transferError

        toast({
          title: 'Success',
          description: 'Caregiver transferred to new specialist',
        })
      } else {
        // Create new assignment using RPC function
        const { error: assignError } = await supabase.rpc(
          'assign_specialist_to_caregiver' as any,
          {
            p_caregiver_id: user.caregiver_id,
            p_specialist_id: selectedSpecialistId,
          }
        )

        if (assignError) throw assignError

        toast({
          title: 'Success',
          description: 'Caregiver assigned to specialist',
        })
      }

      onSuccess()
      onOpenChange(false)
    } catch (error) {
      console.error('Error assigning specialist:', error)
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to assign specialist',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {user.specialist_name ? 'Reassign' : 'Assign'} Specialist
          </DialogTitle>
          <DialogDescription>
            {user.specialist_name
              ? `Currently assigned to: ${user.specialist_name}`
              : 'This caregiver is not currently assigned to a specialist'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="caregiver">Caregiver</Label>
            <div className="text-sm text-muted-foreground">
              {user.user_name} ({user.email})
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="specialist">
              {user.specialist_name ? 'New Specialist' : 'Specialist'}
            </Label>
            {fetchingSpecialists ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            ) : (
              <Select
                value={selectedSpecialistId}
                onValueChange={setSelectedSpecialistId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a specialist" />
                </SelectTrigger>
                <SelectContent>
                  {specialists.map((specialist) => (
                    <SelectItem key={specialist.id} value={specialist.id}>
                      {specialist.name} ({specialist.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {user.specialist_name && (
            <div className="space-y-2">
              <Label htmlFor="reason">Reason (optional)</Label>
              <Textarea
                id="reason"
                placeholder="Enter reason for transfer..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button onClick={handleAssign} disabled={loading || fetchingSpecialists}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {user.specialist_name ? 'Reassign' : 'Assign'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
